import React, { useCallback, useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SettingsIcon, ShieldCheckIcon, CookieIcon } from 'lucide-react';
interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}
const COOKIE_NAME = 'lens_light_cookie_consent';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60 * 1000; // 1 year
function getCookiePreferences(): CookiePreferences | null {
  try {
    const raw = document.cookie.split('; ').find(row => row.startsWith(`${COOKIE_NAME}=`));
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(raw.split('=')[1]));
  } catch {
    return null;
  }
}
function setCookiePreferences(prefs: CookiePreferences) {
  const expires = new Date(Date.now() + COOKIE_MAX_AGE).toUTCString();
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(prefs))}; expires=${expires}; path=/; SameSite=Lax`;
}
export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false
  });
  useEffect(() => {
    const saved = getCookiePreferences();
    if (!saved) {
      // Small delay so the page loads first
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
    setPreferences(saved);
  }, []);
  const handleAcceptAll = useCallback(() => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setCookiePreferences(allAccepted);
    setPreferences(allAccepted);
    setVisible(false);
    setShowSettings(false);
  }, []);
  const handleRejectOptional = useCallback(() => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setCookiePreferences(onlyNecessary);
    setPreferences(onlyNecessary);
    setVisible(false);
    setShowSettings(false);
  }, []);
  const handleSaveSettings = useCallback(() => {
    setCookiePreferences(preferences);
    setVisible(false);
    setShowSettings(false);
  }, [preferences]);
  return <>
      {/* Cookie Banner */}
      <AnimatePresence data-id="element-146">
        {visible && !showSettings && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed bottom-0 left-0 right-0 z-[55] p-4 md:p-6" data-id="element-147">
            <div className="max-w-5xl mx-auto bg-white border border-[#e2e2e6] rounded-2xl shadow-2xl p-6 md:p-8" data-id="element-148">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-149">
                <div className="flex-1" data-id="element-150">
                  <div className="flex items-center gap-3 mb-3" data-id="element-151">
                    <div className="w-8 h-8 rounded-full bg-brand-accent/10 flex items-center justify-center" data-id="element-152">
                      <CookieIcon size={16} className="text-brand-accent" data-id="element-153" />
                    </div>
                    <h3 className="font-heading text-lg text-[#1a1a2e]" data-id="element-154">
                      Wir respektieren deine Privatsphäre
                    </h3>
                  </div>
                  <p className="text-sm text-[#4a4a5a] font-light leading-relaxed" data-id="element-155">
                    Wir verwenden Cookies, um dein Erlebnis auf unserer Website
                    zu verbessern. Notwendige Cookies sind für die Funktion der
                    Website erforderlich. Optionale Cookies helfen uns, die
                    Website zu analysieren und zu verbessern.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0" data-id="element-156">
                  <button onClick={() => setShowSettings(true)} className="px-5 py-2.5 text-sm font-medium text-[#4a4a5a] border border-[#e2e2e6] rounded-lg hover:border-[#c0c0c8] hover:text-[#1a1a2e] transition-colors" data-id="element-157">
                    Einstellungen
                  </button>
                  <button onClick={handleRejectOptional} className="px-5 py-2.5 text-sm font-medium text-[#4a4a5a] border border-[#e2e2e6] rounded-lg hover:border-[#c0c0c8] hover:text-[#1a1a2e] transition-colors" data-id="element-158">
                    Nur notwendige
                  </button>
                  <button onClick={handleAcceptAll} className="px-5 py-2.5 text-sm font-medium text-white bg-brand-accent/85 rounded-lg hover:bg-brand-accent transition-colors" data-id="element-159">
                    Alle akzeptieren
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence data-id="element-160">
        {showSettings && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]" onClick={() => setShowSettings(false)} data-id="element-161" />
            <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} transition={{
          duration: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[85vh] bg-white rounded-2xl shadow-2xl z-[61] overflow-hidden flex flex-col" data-id="element-162">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-[#e2e2e6]" data-id="element-163">
                <div className="flex items-center gap-3" data-id="element-164">
                  <SettingsIcon size={18} className="text-brand-accent" data-id="element-165" />
                  <h2 className="text-xl font-heading text-[#1a1a2e]" data-id="element-166">
                    Cookie-Einstellungen
                  </h2>
                </div>
                <button onClick={() => setShowSettings(false)} className="w-9 h-9 rounded-full flex items-center justify-center text-[#8a8a9a] hover:text-[#1a1a2e] hover:bg-[#f5f5f7] transition-colors" aria-label="Schließen" data-id="element-167">
                  <XIcon size={18} data-id="element-168" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-6 space-y-6 flex-1" data-id="element-169">
                <p className="text-sm text-[#4a4a5a] font-light leading-relaxed" data-id="element-170">
                  Hier kannst du deine Cookie-Einstellungen anpassen. Notwendige
                  Cookies können nicht deaktiviert werden, da sie für die
                  grundlegende Funktion der Website erforderlich sind.
                </p>

                {/* Necessary */}
                <div className="border border-[#e2e2e6] rounded-xl p-5" data-id="element-171">
                  <div className="flex items-center justify-between mb-3" data-id="element-172">
                    <div className="flex items-center gap-3" data-id="element-173">
                      <ShieldCheckIcon size={18} className="text-green-600" data-id="element-174" />
                      <h3 className="font-medium text-[#1a1a2e] text-sm" data-id="element-175">
                        Notwendige Cookies
                      </h3>
                    </div>
                    <span className="text-xs text-green-600 bg-green-50 px-2.5 py-1 rounded-full font-medium" data-id="element-176">
                      Immer aktiv
                    </span>
                  </div>
                  <p className="text-xs text-[#8a8a9a] font-light leading-relaxed" data-id="element-177">
                    Diese Cookies sind für die grundlegende Funktion der Website
                    erforderlich. Sie speichern z.B. deine Cookie-Einstellungen
                    und ermöglichen die Navigation.
                  </p>
                </div>

                {/* Analytics */}
                <div className="border border-[#e2e2e6] rounded-xl p-5" data-id="element-178">
                  <div className="flex items-center justify-between mb-3" data-id="element-179">
                    <h3 className="font-medium text-[#1a1a2e] text-sm" data-id="element-180">
                      Analyse-Cookies
                    </h3>
                    <button onClick={() => setPreferences(p => ({
                  ...p,
                  analytics: !p.analytics
                }))} className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${preferences.analytics ? 'bg-brand-accent' : 'bg-[#e2e2e6]'}`} aria-label="Analyse-Cookies umschalten" data-id="element-181">
                      <motion.div animate={{
                    x: preferences.analytics ? 20 : 2
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm" data-id="element-182" />
                    </button>
                  </div>
                  <p className="text-xs text-[#8a8a9a] font-light leading-relaxed" data-id="element-183">
                    Diese Cookies helfen uns zu verstehen, wie Besucher mit der
                    Website interagieren. Alle Daten werden anonymisiert
                    erfasst.
                  </p>
                </div>

                {/* Marketing */}
                <div className="border border-[#e2e2e6] rounded-xl p-5" data-id="element-184">
                  <div className="flex items-center justify-between mb-3" data-id="element-185">
                    <h3 className="font-medium text-[#1a1a2e] text-sm" data-id="element-186">
                      Marketing-Cookies
                    </h3>
                    <button onClick={() => setPreferences(p => ({
                  ...p,
                  marketing: !p.marketing
                }))} className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${preferences.marketing ? 'bg-brand-accent' : 'bg-[#e2e2e6]'}`} aria-label="Marketing-Cookies umschalten" data-id="element-187">
                      <motion.div animate={{
                    x: preferences.marketing ? 20 : 2
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm" data-id="element-188" />
                    </button>
                  </div>
                  <p className="text-xs text-[#8a8a9a] font-light leading-relaxed" data-id="element-189">
                    Diese Cookies werden verwendet, um Werbung relevanter für
                    dich zu gestalten. Sie können auch dazu dienen, die
                    Häufigkeit von Anzeigen zu begrenzen.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-5 border-t border-[#e2e2e6] flex flex-col sm:flex-row gap-3" data-id="element-190">
                <button onClick={handleRejectOptional} className="flex-1 px-5 py-2.5 text-sm font-medium text-[#4a4a5a] border border-[#e2e2e6] rounded-lg hover:border-[#c0c0c8] hover:text-[#1a1a2e] transition-colors" data-id="element-191">
                  Nur notwendige
                </button>
                <button onClick={handleSaveSettings} className="flex-1 px-5 py-2.5 text-sm font-medium text-[#1a1a2e] border border-brand-accent rounded-lg hover:bg-brand-accent/5 transition-colors" data-id="element-192">
                  Auswahl speichern
                </button>
                <button onClick={handleAcceptAll} className="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-brand-accent/85 rounded-lg hover:bg-brand-accent transition-colors" data-id="element-193">
                  Alle akzeptieren
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}